/**
 * The ink: every causal connector is one thin line from its card to its card,
 * rounded at every corner. Where several run on the same stretch, out of one
 * card or into one, each is shifted a pixel sideways from the next, so together
 * they are a ribbon as thick as their count, each colour showing at its edge,
 * and lie in it as cables in a loom: a late joiner on the outside, an early
 * leaver on the side it leaves to, so joins and exits show as curves at the
 * edge. One line is one object: it is selected, followed and tapped whole.
 *
 * A shift belongs to a whole segment, so a corner between a shifted horizontal
 * and a shifted vertical is still a right angle: the drawn route is axis-aligned
 * and orthD rounds it like any other. A segment shared by runs of different
 * counts takes its shift from the thickest run; elsewhere on it the line is off
 * its nominal course by a pixel or two, which no one sees.
 *
 * Pure: routed edges in, { id → { points, path } } out.
 */
import { orthD } from './routeEdges';

const r2 = (n) => Math.round(n * 100) / 100;

export function inkRoutes(edges, config) {
  const spacing = config.inkSpacing;
  const routes = edges.filter((e) => !e.behind && e.points?.length > 1);
  // Every axis-aligned segment, grouped by the line it lies on, with where its
  // route came onto the line and where it goes off it.
  const lines = new Map();
  routes.forEach((e) => {
    const pts = e.points;
    for (let i = 1; i < pts.length; i += 1) {
      const a = pts[i - 1];
      const b = pts[i];
      const horizontal = Math.abs(a.y - b.y) < 0.01;
      const k = horizontal ? `h:${r2(a.y)}` : `v:${r2(a.x)}`;
      if (!lines.has(k)) lines.set(k, []);
      const along = (p) => (horizontal ? p.x : p.y);
      const across = (p) => (horizontal ? p.y : p.x);
      const dir = Math.sign(along(b) - along(a)) || 1;
      const prev = pts[i - 2];
      const next = pts[i + 1];
      lines.get(k).push({
        lo: Math.min(along(a), along(b)), hi: Math.max(along(a), along(b)), e, i,
        joinAt: prev ? dir * along(a) : -Infinity, joinSide: prev ? Math.sign(across(prev) - across(a)) : 0,
        leaveAt: next ? dir * along(b) : Infinity, leaveSide: next ? Math.sign(across(next) - across(b)) : 0,
        last: dir * along(b),
      });
    }
  });
  // A place in the ribbon for each route on a shared run, as cables lie in a loom:
  // a route that joins later or leaves earlier lies further out, on the side it
  // comes from — or, when it leaves before the others, on the side it goes to —
  // so every join and every exit is a curve at the ribbon's edge. A segment keeps
  // the place of its thickest run.
  const shift = new Map(); // `${id}:${i}` → { n, d }
  lines.forEach((segs) => {
    const end = Math.max(...segs.map((s) => s.last));
    const cuts = [...new Set(segs.flatMap((s) => [r2(s.lo), r2(s.hi)]))].sort((p, q) => p - q);
    for (let c = 1; c < cuts.length; c += 1) {
      const lo = cuts[c - 1];
      const hi = cuts[c];
      const cover = segs.filter((s) => s.lo <= lo + 0.01 && s.hi >= hi - 0.01);
      if (cover.length < 2) continue;
      const side = (s) => (s.leaveAt < end - 0.01 && s.leaveSide ? s.leaveSide : (s.joinSide || s.leaveSide));
      const out = (s) => (s.joinAt === -Infinity ? -1e9 : s.joinAt) - (s.leaveAt === Infinity ? 1e9 : s.leaveAt); // larger: further out
      const byId = (p, q) => p.e.id.localeCompare(q.e.id);
      const above = cover.filter((s) => side(s) < 0).sort((p, q) => out(q) - out(p) || byId(p, q));
      const middle = cover.filter((s) => side(s) === 0).sort(byId);
      const below = cover.filter((s) => side(s) > 0).sort((p, q) => out(p) - out(q) || byId(p, q));
      [...above, ...middle, ...below].forEach((s, k) => {
        const key = `${s.e.id}:${s.i}`;
        const d = (k - (cover.length - 1) / 2) * spacing;
        if (!shift.has(key) || shift.get(key).n < cover.length) shift.set(key, { n: cover.length, d });
      });
    }
  });
  // The drawn route: each segment moved sideways by its shift, corners where the
  // moved segments meet, the ends on the moved first and last segments.
  const out = new Map();
  routes.forEach((e) => {
    const pts = e.points;
    const seg = (i) => { // the shift of segment i (from pts[i-1] to pts[i]) as dx, dy
      const d = shift.get(`${e.id}:${i}`)?.d || 0;
      const horizontal = Math.abs(pts[i - 1].y - pts[i].y) < 0.01;
      return horizontal ? { dx: 0, dy: d } : { dx: d, dy: 0 };
    };
    const points = pts.map((p, i) => {
      const before = i > 0 ? seg(i) : null;
      const after = i < pts.length - 1 ? seg(i + 1) : null;
      return { x: r2(p.x + (before?.dx || 0) + (after?.dx || 0)), y: r2(p.y + (before?.dy || 0) + (after?.dy || 0)) };
    });
    out.set(e.id, { points, path: orthD(points, config) });
  });
  return out;
}
